import { useState, useCallback, useMemo, useEffect } from "react";
import axios from "axios";
import config from '../config/config';
import { useCart } from '../context/CartContext';
/* ─── Font ──────────────────────────────────────────────────────────────────── */
const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap";

/* ─── Responsive hook ───────────────────────────────────────────────────────── */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {

    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

/* ─── Demo / fallback data ──────────────────────────────────────────────────── */
const DEMO_PRODUCT = {
  id: '',
  name: "Available Options",
  unitPrice: 0.00,
};

 const featuredBg = localStorage.getItem('featuredBg');
  const userInfor = localStorage.getItem('userInfor');



const DEMO_MAIN_VARIANTS = [
  {
    id: 1,
    name: "Color",
    type: "color",
    subVariants: [
      { id: 101, label: "Black", colorHex: "#1a1a1a" },
      { id: 102, label: "Arctic White", colorHex: "#d4d4d0" },
      { id: 103, label: "Navy Blue", colorHex: "#1e3a5f" },
      { id: 104, label: "Forest Green", colorHex: "#3a7d5a" },
      { id: 105, label: "Burgundy", colorHex: "#7c2d3e" },
    ],
  },
  {
    id: 2,
    name: "Size",
    type: "text",
    subVariants: [
      { id: 201, label: "XS" },
      { id: 202, label: "S" },
      { id: 203, label: "M" },
      { id: 204, label: "L" },
      { id: 205, label: "XL" },
      { id: 206, label: "XXL" },
    ],
  },
];

/* ─── Helpers ───────────────────────────────────────────────────────────────── */
const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KSH" }).format(n);

const stateKey = (a: number | string, b: number | string) => `${a}_${b}`;

function buildPayload(
  productId: number | string,
  mainVariants: typeof DEMO_MAIN_VARIANTS,
  quantities: Record<string, number>
) {
  const payload: any[] = [];
  if (mainVariants.length < 2) return payload;

  const [rowV, colV] = mainVariants;

  rowV.subVariants.forEach((r) => {
    colV.subVariants.forEach((c) => {
      const qty = parseInt(quantities[stateKey(r.id, c.id)]?.toString() || "0", 10);
      if (qty > 0) {
        payload.push({
          product_id: productId,
          main_variant_id: r.id,
          sub_variant_id: c.id,
          quantity: qty,
          _main_variant_label: r.label,
          _sub_variant_label: c.label,
        });
      }
    });
  });

  return payload;
}

const BASE_URL = config.API_URL+'/api';

async function saveVariants(
  productId: number | string,
  mainVariants: typeof DEMO_MAIN_VARIANTS,
  quantities: Record<string, number>
) {
  const payload = buildPayload(productId, mainVariants, quantities);
  if (!payload.length) throw new Error("No quantities to save.");
  const token = localStorage.getItem("token");
   await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];
 
  const res = await fetch(`${BASE_URL}/variant-quantities/${productId}`, {
    method: "POST",
    credentials: "include", 
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
  },
    body: JSON.stringify({ variants: payload }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Server error ${res.status}`);
  }

  return res.json();
}

/* ─── Atoms ─────────────────────────────────────────────────────────────────── */
function ColorDot({ hex, size = 12 }: { hex: string; size?: number }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: hex,
        border: "1.5px solid rgba(0,0,0,0.14)",
        flexShrink: 0,
      }}
    />
  );
}

function QtyInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const active = value > 0;
  return (
    <input
      type="number"
      min={0}
      max={9999}
      step={1}
      value={value || ""}
      placeholder="—"
      onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
      style={{
        width: 52,
        padding: "5px 2px",
        border: `1.5px solid ${active ? "#93c5fd" : "#333"}`,
        borderRadius: 8,
       
        color: "black",
        fontFamily: "Quicksand, sans-serif",
        fontWeight: 700,
        fontSize: 13,
        textAlign: "center",
        outline: "none",
        transition: "all .15s",
        boxSizing: "border-box",
      }}
    />
  );
}






/* ─── Single shimmer bone ────────────────────────────────────────────────────── */
function Bone({ width = "100%", height = 14, radius = 6, style = {} }) {
  return (
    <div
      className="vm-bone"
      style={{
        width,
        height,
        borderRadius: radius,
        background: "#e9ecef",
        flexShrink: 0,
        ...style,
      }}
    />
  );
}
 
/* ─── Desktop skeleton ───────────────────────────────────────────────────────── */
function DesktopSkeleton({ rows = 5, cols = 6 }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
        <thead>
          <tr>
            {/* Corner cell */}
            <th style={dTh.corner}>
              <Bone width={60} height={10} />
              <div style={{ height: 1, background: "#e5e7eb", margin: "5px 0", transform: "rotate(-8deg)" }} />
              <Bone width={44} height={10} />
            </th>
            {/* Column headers */}
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} style={dTh.col}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Bone width={28} height={11} radius={4} />
                </div>
              </th>
            ))}
            {/* Total header */}
            <th style={dTh.total}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Bone width={36} height={11} radius={4} />
              </div>
            </th>
          </tr>
        </thead>
 
        <tbody>
          {Array.from({ length: rows }).map((_, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "#fafafa" }}>
              {/* Row label */}
              <td style={dTd.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Bone width={13} height={13} radius={50} />
                  <Bone width={72} height={13} />
                </div>
              </td>
              {/* Qty cells */}
              {Array.from({ length: cols }).map((_, ci) => (
                <td key={ci} style={dTd.qty}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Bone width={52} height={30} radius={8} />
                  </div>
                </td>
              ))}
              {/* Row total */}
              <td style={dTd.rowTotal}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Bone width={24} height={13} radius={4} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
 
        <tfoot>
          <tr>
            <td style={dTd.footLabel}>
              <Bone width={56} height={10} radius={4} />
            </td>
            {Array.from({ length: cols }).map((_, i) => (
              <td key={i} style={dTd.footCell}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Bone width={20} height={13} radius={4} />
                </div>
              </td>
            ))}
            <td style={dTd.footCell}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Bone width={24} height={14} radius={4} />
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
 
/* ─── Mobile skeleton ────────────────────────────────────────────────────────── */
function MobileSkeleton({ rows = 5, cols = 6 }) {
  return (
    <div style={{ padding: "12px 12px 6px", display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, ri) => (
        <div key={ri} style={{
          border: "1.5px solid #ebebeb",
          borderRadius: 14, background: "#fff", overflow: "hidden",
        }}>
          {/* Accordion header bone */}
          <div style={{
            padding: "13px 14px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <Bone width={16} height={16} radius={50} />
              <Bone width={80 + ri * 8} height={14} />
            </div>
            <Bone width={16} height={16} radius={4} />
          </div>
 
          {/* Size grid bones — show on first two cards only */}
          {ri < 2 && (
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 8, padding: "4px 12px 14px",
            }}>
              {Array.from({ length: cols }).map((_, ci) => (
                <div key={ci} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "9px 10px", borderRadius: 10, background: "#f8fafc",
                  border: "1px solid #e9edf2",
                }}>
                  <Bone width={24} height={13} />
                  {/* Stepper bones */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Bone width={32} height={32} radius={8} />
                    <Bone width={20} height={14} radius={4} />
                    <Bone width={32} height={32} radius={8} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
 
/* ─── Stats bar skeleton ─────────────────────────────────────────────────────── */
function StatsBarSkeleton({ isMobile }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: 10,
      padding: isMobile ? "12px 14px" : "12px 16px",
      borderTop: "1px solid #f0f0f0", background: "#fff",
    }}>
      <div style={{ display: "flex", gap: 8 }}>
        {[52, 40, 76].map((w, i) => (
          <div key={i} style={{
            padding: isMobile ? "7px 11px" : "8px 14px",
            background: "#f8faff", borderRadius: 10, border: "1px solid #e8f0fe",
            display: "flex", flexDirection: "column", gap: 5,
          }}>
            <Bone width={w * 0.6} height={9} radius={3} />
            <Bone width={w} height={isMobile ? 15 : 18} radius={4} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Bone width={64}  height={38} radius={10} />
        <Bone width={120} height={38} radius={10} />
      </div>
    </div>
  );
}
 
/* ─── Summary panel skeleton ─────────────────────────────────────────────────── */
function SummaryPanelSkeleton({ isMobile }) {
  return (
    <div style={{
      background: "#fafbff",
      borderTop:  isMobile ? "1px solid #f0f0f0" : "none",
      borderLeft: isMobile ? "none" : "1px solid #f0f0f0",
      width: isMobile ? "100%" : 260,
      flexShrink: 0,
    }}>
      <div style={{ padding: "18px 16px 20px" }}>
        {/* Title bone */}
        <Bone width={100} height={10} radius={4} style={{ marginBottom: 16 }} />
 
        {/* Line item bones */}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 10px", borderRadius: 10,
              background: "#fff", border: "1px solid #e8f0fe",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Bone width={9} height={9} radius={50} />
                <Bone width={90 - i * 8} height={12} />
              </div>
              <Bone width={44} height={12} radius={4} />
            </div>
          ))}
        </div>
 
        {/* Totals bones */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e8f0fe", display: "flex", flexDirection: "column", gap: 9 }}>
          {[80, 60, 90].map((w, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Bone width={36} height={11} radius={4} />
              <Bone width={w * 0.55} height={i === 2 ? 15 : 12} radius={4} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VariantMatrixSkeleton({ rows = 5, cols = 6 }) {
  const isMobile = useIsMobile(640);
 
  return (
    <>
      <style>{`
        @import url('${FONT_URL}');
        * { box-sizing: border-box; }
 
        @keyframes vm-shimmer {
          0%   { opacity: 1;    }
          50%  { opacity: 0.45; }
          100% { opacity: 1;    }
        }
 
        .vm-bone {
          animation: vm-shimmer 1.6s ease-in-out infinite;
        }
      `}</style>
 
      <div style={{
        fontFamily: "Quicksand, sans-serif",
        background: "#fff",
        borderRadius: isMobile ? 16 : 20,
        border: "1px solid #eaecf0",
        boxShadow: "0 2px 20px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.03)",
        overflow: "hidden",
        width: "100%",
        maxWidth: 960,
        margin: "0 auto",
      }}>
 
        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          flexWrap: "wrap", gap: 10,
          padding: isMobile ? "14px 14px 12px" : "16px 20px 14px",
          borderBottom: "1px solid #f0f0f0", background: "#fafafa",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <Bone width={isMobile ? 160 : 200} height={isMobile ? 15 : 17} radius={5} />
            <Bone width={140} height={11} radius={4} />
          </div>
          <Bone width={96} height={24} radius={20} />
        </div>
 
        {/* ── Mobile tab bar ── */}
        {isMobile && (
          <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0" }}>
            {[0, 1].map((i) => (
              <div key={i} style={{
                flex: 1, padding: "12px 0",
                display: "flex", justifyContent: "center",
                borderBottom: i === 0 ? "2.5px solid #e9ecef" : "2.5px solid transparent",
              }}>
                <Bone width={i === 0 ? 52 : 68} height={13} radius={5} />
              </div>
            ))}
          </div>
        )}
 
        {/* ── Body ── */}
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "flex-start",
        }}>
          {/* Matrix panel */}
          <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? "none" : "1px solid #f0f0f0" }}>
            {isMobile
              ? <MobileSkeleton rows={rows} cols={cols} />
              : <DesktopSkeleton rows={rows} cols={cols} />
            }
            <StatsBarSkeleton isMobile={isMobile} />
          </div>
 
          {/* Summary panel */}
          {!isMobile && <SummaryPanelSkeleton isMobile={isMobile} />}
        </div>
      </div>
    </>
  );
}






function Stepper({
  value,
  onChange,
  r_id,
  c_id,
  setQty,
}: {
  value: number;
  onChange: (v: number) => void;
  r_id:r_id,
  c_id:c_id,
setQty: (key: string, val: number) => void;
}) {
  const active = value > 0;
  const btnStyle = {
    width: 32,
    height: 32,
    border: "none",
    borderRadius: 8,
    background: active ? featuredBg : "#f1f5f9",
    color: active ? "#ffffff" : "black",
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  return (
    <div className="w-full" style={{ display: "flex", alignItems: "center", gap: 8 ,width:'100%'}}>
      <button style={btnStyle} onClick={() => onChange(Math.max(0, value - 1))}>
        −
      </button>
       <QtyInput
                    value={value}
                    onChange={(v) => setQty(stateKey(r_id, c_id), v)}

                  />
      <button style={btnStyle} onClick={() => onChange(Math.min(9999, value + 1))}>
        +
      </button>
    </div>
  );
}

/* ─── Desktop matrix table ──────────────────────────────────────────────────── */
function DesktopMatrix({
  rowVariant,
  colVariant,
  getQty,
  setQty,
  rowTotals,
  colTotals,
  totalUnits,
  featuredBg,
}: {
  rowVariant: any;
  colVariant: any;
  getQty: (r: any, c: any) => number;
  setQty: (key: string, val: number) => void;
  rowTotals: Record<any, number>;
  colTotals: Record<any, number>;
  totalUnits: number;
  featuredBg:featuredBg;
}) {
  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
        <thead>
          <tr>
            <th style={dTh.corner}>
            
              <span style={{ fontSize: 10, color: "#bbb", fontWeight: 600 }}>{rowVariant.name}</span>
              <div style={{ height: 1, background: "#e5e7eb", margin: "3px 0", transform: "rotate(-8deg)" }} />
              <span style={{ fontSize: 10, color: "#bbb", fontWeight: 600 }}>{colVariant.name}</span>
            </th>
            {colVariant.subVariants.map((c: any) => (
              <th key={c.id} style={dTh.col}>{c.label}</th>
            ))}
            <th style={dTh.total}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rowVariant.subVariants.map((r: any, ri: number) => (
            <tr key={r.id} style={{ background: ri % 2 === 0 ? "#fff" : "#fafafa" }}>
              <td style={dTd.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                 <img
                    src={r.image_url || 'https://placehold.co/400x500?text=No+Image'}
                    alt={r.label}
                    className="rounded shadow-md"
                    style={{width:'75px', height:'75px', borderRadius:'10px',border:'3px solid rgb(241, 245, 249)'}}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/400x500?text=Image+Not+Found';
                    }}
                  />
                  {rowVariant.type === "color" && r.colorHex && <ColorDot hex={r.colorHex} size={12} />}
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{r.label}</span>
                </div>
              </td>
              {colVariant.subVariants.map((c: any) => (
                <td key={c.id} style={dTd.qty}>
                  <QtyInput
                    value={getQty(r.id, c.id)}
                    onChange={(v) => setQty(stateKey(r.id, c.id), v)}

                  />
                </td>
              ))}
              <td style={dTd.rowTotal}>
                <span style={{ fontSize: 13, fontWeight: 700, color: rowTotals[r.id] > 0 ? featuredBg : "#cbd5e1" }}>
                  {rowTotals[r.id] > 0 ? rowTotals[r.id] : "—"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={dTd.footLabel}>Col Total</td>
            {colVariant.subVariants.map((c: any) => (
              <td key={c.id} style={dTd.footCell}>
                <span style={{ fontSize: 13, fontWeight: 700, color: (colTotals[c.id] || 0) > 0 ? featuredBg : "#cbd5e1" }}>
                  {(colTotals[c.id] || 0) > 0 ? colTotals[c.id] : "—"}
                </span>
              </td>
            ))}
            <td style={{ ...dTd.footCell, color: featuredBg, fontSize: 14, fontWeight: 800 }}>
              {totalUnits > 0 ? totalUnits : "—"}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ─── Mobile accordion cards ────────────────────────────────────────────────── */
function MobileMatrix({
  rowVariant,
  colVariant,
  getQty,
  setQty,
  rowTotals,
  featuredBg,
}: {
  rowVariant: any;
  colVariant: any;
  getQty: (r: any, c: any) => number;
  setQty: (key: string, val: number) => void;
  rowTotals: Record<any, number>;
  featuredBg:featuredBg;
}) {
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(rowVariant.subVariants.map((r: any) => [r.id, true]))
  );
  const toggle = (id: any) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div style={{ padding: "12px 12px 6px", display: "flex", flexDirection: "column", gap: 10 }}>
      {rowVariant.subVariants.map((r: any) => {
        const rowTotal = rowTotals[r.id] || 0;
        const open = expanded[r.id];
        return (
          <div
            key={r.id}
            style={{
              border: `1.5px solid ${rowTotal > 0 ? featuredBg : "#ebebeb"}`,
              borderRadius: 14,
              

              overflow: "hidden",
              transition: "border-color .2s, background .2s",
            }}
          >

            <button
              onClick={() => toggle(r.id)}
              style={{
                width: "100%",
                padding: "13px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
               <img
                    src={r.image_url || 'https://placehold.co/400x500?text=No+Image'}
                    alt={r.label}
                    className="rounded shadow-md"
                    style={{width:'75px', height:'75px', borderRadius:'10px',border:'3px solid rgb(241, 245, 249)'}}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/400x500?text=Image+Not+Found';
                    }}
                  />
                {rowVariant.type === "color" && r.colorHex && <ColorDot hex={r.colorHex} size={16} />}
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{r.label}</span>
                {rowTotal > 0 && (
                  <span
                    style={{
                      padding: "2px 9px",
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      background: featuredBg,
                      color: "#ffffff",
                    }}
                  >
                    {rowTotal} units
                  </span>
                )}
              </div>
              <span
                style={{
                  fontSize: 20,
                  color: "#94a3b8",
                  lineHeight: 1,
                  transform: open ? "rotate(90deg)" : "rotate(-90deg)",
                  transition: "transform .2s",
                  display: "inline-block",
                }}
              >
                ‹
              </span>
            </button>

            {open && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 8,
                  width:"100%",
                  padding: "4px 12px 14px",
                }}
              >
                {colVariant.subVariants.map((c: any) => {
                  const qty = getQty(r.id, c.id);
                  return (
                    <div
                      key={c.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "9px 10px",
                        borderRadius: 10,
                       width:"100%",
                        border: `1px solid ${qty > 0 ? "#bfdbfe" : "#e9edf2"}`,
                        transition: "all .15s",
                      }}
                    >
                     
                      <span className="gap-3" style={{ fontSize: 13,marginRight:'10px', fontWeight: 700, color: qty > 0 ? 'black' : "#6b7280" }}>
                        {c.label}
                      </span>

                      <Stepper
                        value={qty}

                        onChange={(v) => setQty(stateKey(r.id, c.id), v)}
                        r_id={r.id}
                        c_id={c.id}
                        setQty={setQty}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Stats + action bar ─────────────────────────────────────────────────────── */
function StatsBar({
  totalUnits,
  totalSKUs,
  orderTotal,
  onClear,
  onSave,
  saving,
  isMobile,
  featuredBg,
  changeQuantity,
  product,
  setToast,
}: {
  totalUnits: number;
  totalSKUs: number;
  orderTotal: number;
  onClear: () => void;
  onSave: () => void;
  saving: boolean;
  isMobile: boolean;
  featuredBg: featuredBg;
  changeQuantity:changeQuantity;
  product:product;
  setToast:setToast;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        padding: isMobile ? "12px 14px" : "12px 16px",
        borderTop: "1px solid #f0f0f0",
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          ["Units", totalUnits, true],
          ["SKUs", totalSKUs, false],
          ["Total", fmt(orderTotal), true],
        ].map(([label, val, accent]) => (
          <div
            key={label}
            style={{
              padding: isMobile ? "7px 11px" : "8px 14px",
              background: "#f8faff",
              borderRadius: 10,
              border: "1px solid #e8f0fe",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
                color: "#94a3b8",
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: isMobile ? 15 : 18,
                fontWeight: 700,
                color: accent ? featuredBg : featuredBg,
              }}
            >
              {val}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onClear}
          disabled={totalUnits === 0}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "red",
            fontFamily: "Quicksand, sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: totalUnits === 0 ? "not-allowed" : "pointer",
            opacity: totalUnits === 0 ? 0.4 : 1,
          }}
        >
          Clear
        </button>
        <button
          onClick={async () => {
  try {
    
    await onSave();   
   //alert(`Saved ${totalUnits} units.`);  
    changeQuantity(product?.id, totalUnits,product);
    
  } catch (err) {
    console.error("Save failed, not updating cart", err);
    // optionally show error toast
  }
}}
          disabled={saving || totalUnits === 0}
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            border: "none",
            backgroundColor:  featuredBg ,
            color: saving || totalUnits === 0 ? "#fff" : "#fff",
            fontFamily: "Quicksand, sans-serif",
            fontSize: 13,
            fontWeight: 700,
            cursor: saving || totalUnits === 0 ? "not-allowed" : "pointer",
            letterSpacing: "0.03em",
            transition: "background .15s",
            whiteSpace: "nowrap",
          }}
        >
          {saving ? "Saving…" : totalUnits > 0 ? `Add To Cart  ${totalUnits} item(s)` : `Add To Cart  ${totalUnits} item(s)`}
        </button>
      </div>
    </div>
  );
}

/* ─── Summary sidebar / panel ───────────────────────────────────────────────── */
function SummaryPanel({
  lineItems,
  rowVariant,
  totalUnits,
  totalSKUs,
  orderTotal,
  unitPrice,
  isMobile,
  featuredBg,
}: {
  lineItems: any[];
  rowVariant: any;
  totalUnits: number;
  totalSKUs: number;
  orderTotal: number;
  unitPrice: number;
  isMobile: boolean;
  featuredBg:featuredBg;
}) {
  return (
    <div
      style={{
        background: "#fafbff",
        borderTop: isMobile ? "1px solid #f0f0f0" : "none",
        borderLeft: isMobile ? "none" : "1px solid #f0f0f0",
        width: isMobile ? "100%" : 460,
        flexShrink: 0,
      }}
    >
      <div style={{ padding: "18px 16px 20px" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "#94a3b8",
            marginBottom: 12,
          }}
        >
          Order Summary
        </div>

        {lineItems.length === 0 ? (
          <p style={{ fontSize: 12, color: "#c8c8c8", fontWeight: 500, lineHeight: 1.7 }}>
            Enter quantities to see your order here.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {lineItems.map((item, i) => {
              const rowSub = rowVariant.subVariants.find((r: any) => r.id === item.main_variant_id);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "7px 10px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid #e8f0fe",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    {rowVariant.type === "color" && rowSub?.colorHex && (
                      <ColorDot hex={rowSub.colorHex} size={9} />
                    )}
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#374151",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item._main_variant_label}
                      <br/>
                       Ksh {unitPrice} × {item.quantity} (Qty)
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "black" }}>
                    
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: featuredBg }}>
                      = {fmt(item.quantity * unitPrice)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {lineItems.length > 0 && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: "1px solid #e8f0fe",
              display: "flex",
              flexDirection: "column",
              gap: 7,
            }}
          >
            {[
              ["Units", totalUnits, true],
              ["SKUs", totalSKUs, true],
              ["Total", fmt(orderTotal), true],
            ].map(([l, v, a]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 14, color: "black", fontWeight: 600 }}>{l}</span>
                <span
                  style={{
                    fontSize: l === "Total" ? 15 : 13,
                    fontWeight: 700,
                    color: a ? featuredBg : "#374151",
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        )}

       
       
      </div>
    </div>
  );
}

/* ─── Root Component ────────────────────────────────────────────────────────── */
interface VariantGroup {
  id: number | string;
  name: string;
  type: string;
  subVariants: Array<{
    id: number | string;
    label: string;
    colorHex?: string;
  }>;
}

interface BulkTableProps {
  product?: {
    id: number | string;
    name: string;
    unitPrice?: number;
  };
  allVariants?: VariantGroup[];
  featuredBg:featuredBg;
  onSaveSuccess?: (data: any) => void;
  onSaveError?: (err: Error) => void;
}



export default function BulkTable({
  product = DEMO_PRODUCT,
  allVariants = DEMO_MAIN_VARIANTS,
  featuredBg=featuredBg,
  onSaveSuccess,
  onSaveError,
}: BulkTableProps) {

  const isMobile = useIsMobile(640);
   const [quantities, setQuantities] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast]           = useState(null);
  const [isLoading,setLoading]=useState(true);
 const {
    addToCart,
    changeQuantity,
    isInCart,
    getQuantity,
  } = useCart();


  const token = localStorage.getItem("token");
  const pullVariants = async (status) => {
    try {
     
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];
      const endpoint = '/api/product-variants/' + product?.id;

      const response = await axios.get(
        config.API_URL + endpoint,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

     
        setQuantities(response.data.quantities);
        setLoading(false);
     
    } catch (error) {
      //alert("Failed to save.");
    }
  };

  useEffect(() => {
    pullVariants();
  },[]);

  const setQty = useCallback((key: string, val: number) => {
    setQuantities((prev) => ({ ...prev, [key]: val }));
  }, []);

  const getQty = useCallback(
    (rId: number | string, cId: number | string) => quantities[stateKey(rId, cId)] || 0,
    [quantities]
  );
  

  const rowVariant = allVariants[0];
  const colVariant = allVariants[1];
   //console.log(featuredBg);

   const unitPrice = product?.price ?? 0;

   

  

  const { totalUnits, totalSKUs, orderTotal, rowTotals, colTotals } = useMemo(() => {
    let tu = 0;
    let ts = 0;
    const rt: Record<string | number, number> = {};
    const ct: Record<string | number, number> = {};

    rowVariant?.subVariants.forEach((r: any) => {
      rt[r.id] = 0;
      colVariant?.subVariants.forEach((c: any) => {
        const v = getQty(r.id, c.id);
        rt[r.id] += v;
        ct[c.id] = (ct[c.id] || 0) + v;
        tu += v;
        if (v > 0) ts++;
      });
    });

    return {
      totalUnits: tu,
      totalSKUs: ts,
      orderTotal: tu * unitPrice,
      rowTotals: rt,
      colTotals: ct,
    };
  }, [quantities, rowVariant, colVariant, unitPrice, getQty]);

  const lineItems = useMemo(
    () => buildPayload(product?.id, allVariants, quantities),
    [product?.id, allVariants, quantities]
  );

  if (!rowVariant?.subVariants || !colVariant?.subVariants) {
    return (
      <div style={{ padding: "2rem", color: "#dc2626", fontWeight: 500, textAlign: "center" }}>
      
      </div>
    );
  }

 

  const handleSave = async () => {
    if (!totalUnits) return;
    setSaving(true);
    setToast(null);
    try {
      const data = await saveVariants(product?.id, allVariants, quantities);
      setToast({ type: "success", msg: `Saved ${totalUnits} units across ${totalSKUs} SKUs.` });
      onSaveSuccess?.(data);
    } catch (err) {
      const message = err.message || "Failed to save variants";
      setToast({ type: "error", msg: message });
      onSaveError?.(err);
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4500);
    }
  };

  const handleClear = () => setQuantities({});
  
   
  {isLoading && (<VariantMatrixSkeleton rows={5} cols={6} />)}


  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('${FONT_URL}');
        * { box-sizing: border-box; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      <div
        style={{
          fontFamily: "Quicksand, sans-serif",
          background: "#fff",
          borderRadius: isMobile ? 16 : 20,
          border: "1px solid #eaecf0",
          boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
          overflow: "hidden",
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        {/* Product header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            padding: isMobile ? "14px 14px 12px" : "16px 20px 14px",
            borderBottom: "1px solid #f0f0f0",
            background: "#fafafa",
          }}
        >
          <div>
            <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#111" }}>
              {product.name} Available Options
            </div>
          </div>
          
        </div>

        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>
          {/* Matrix area */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isMobile ? (
              <MobileMatrix
                rowVariant={rowVariant}
                colVariant={colVariant}
                getQty={getQty}
                setQty={setQty}
                rowTotals={rowTotals}
                featuredBg={featuredBg}
              />
            ) : (
              <DesktopMatrix
                rowVariant={rowVariant}
                colVariant={colVariant}
                getQty={getQty}
                setQty={setQty}
                rowTotals={rowTotals}
                colTotals={colTotals}
                totalUnits={totalUnits}
                featuredBg={featuredBg}
              />
            )}

            <StatsBar
              totalUnits={totalUnits}
              totalSKUs={totalSKUs}
              orderTotal={orderTotal}
              onClear={handleClear}
              onSave={handleSave}
              saving={saving}
              isMobile={isMobile}
               featuredBg={featuredBg}
               changeQuantity={changeQuantity}
               product={product}
               setToast={setToast}
            />

            {toast && (
              <div
                style={{
                  margin: "0 14px 14px",
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  background: toast.type === "success" ? "#f0fdf4" : "#fef2f2",
                  color: toast.type === "success" ? "#166534" : "#991b1b",
                  border: `1px solid ${toast.type === "success" ? "#bbf7d0" : "#fecaca"}`,
                }}
              >
                {toast.type === "success" ? "✓ " : "✕ "} {toast.msg}
              </div>
            )}
          </div>

          {/* Summary panel */}
          <SummaryPanel
            lineItems={lineItems}
            rowVariant={rowVariant}
            totalUnits={totalUnits}
            totalSKUs={totalSKUs}
            orderTotal={orderTotal}
            unitPrice={unitPrice}
            isMobile={isMobile}
            featuredBg={featuredBg}
          />
        </div>
      </div>
    </>
  );
}

/* ─── Style tokens ──────────────────────────────────────────────────────────── */
const dTh = {
  corner: {
    textAlign: "left",
    padding: "9px 10px 9px 14px",
    background: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
    minWidth: 110,
    userSelect: "none",
  },
  col: {
    padding: "9px 6px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: featuredBg,
    background: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  total: {
    padding: "9px 10px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: featuredBg,
    background: "#f1f5f9",
    borderBottom: "1px solid #f0f0f0",
    textAlign: "center",
    minWidth: 54,
  },
};

const dTd = {
  label: { padding: "8px 10px 8px 14px", borderBottom: "1px solid #f5f5f5", whiteSpace: "nowrap" },
  qty: { padding: "6px 5px", textAlign: "center", borderBottom: "1px solid #f5f5f5" ,color:featuredBg },
  rowTotal: {
    padding: "6px 10px",
    textAlign: "center",
    borderBottom: "1px solid #f5f5f5",
    background: "#f8faff",
  },
  footLabel: {
    padding: "8px 10px 8px 14px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: featuredBg,
    background: "#f1f5f9",
    borderTop: "1px solid #e2e8f0",
  },
  footCell: { padding: "8px 5px", textAlign: "center", background: "#f1f5f9", borderTop: "1px solid #e2e8f0" },
};